import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Alert } from 'react-bootstrap';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';
import { getStudents } from '../../services/api';

interface DuplicateStudentRecord {
  student_name: string;
  count: number;
}

const DuplicateStudentsCard: React.FC = () => {
  const [duplicates, setDuplicates] = useState<DuplicateStudentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch students and calculate duplicates on mount
  useEffect(() => {
    const fetchAndCalculateDuplicates = async () => {
      setLoading(true);
      setError(null);

      try {
        const students = await getStudents();

        // Group students by lowercase name and count occurrences
        const nameCounts: { [key: string]: number } = {};
        const nameMapping: { [key: string]: string } = {};

        students.forEach(student => {
          const normalizedName = student.name.toLowerCase();
          nameCounts[normalizedName] = (nameCounts[normalizedName] || 0) + 1;
          // Store first occurrence's original casing
          if (!nameMapping[normalizedName]) {
            nameMapping[normalizedName] = student.name;
          }
        });

        // Filter duplicates (count > 1) and create records
        const duplicateRecords: DuplicateStudentRecord[] = Object.entries(nameCounts)
          .filter(([_, count]) => count > 1)
          .map(([normalizedName, count]) => ({
            student_name: nameMapping[normalizedName],
            count: count
          }))
          .sort((a, b) => b.count - a.count || a.student_name.toLowerCase().localeCompare(b.student_name.toLowerCase()));

        setDuplicates(duplicateRecords);
      } catch (err: any) {
        console.error('Error fetching students:', err);
        setError(err.response?.data?.detail || 'Failed to fetch student records');
      } finally {
        setLoading(false);
      }
    };

    fetchAndCalculateDuplicates();
  }, []);

  // Sorting state
  const [sorting, setSorting] = useState<SortingState>([{ id: 'count', desc: true }]);

  // Define columns for the table
  const columnHelper = createColumnHelper<DuplicateStudentRecord>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('student_name', {
        header: 'Student Name',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('count', {
        header: 'Count',
        cell: info => info.getValue(),
      }),
    ],
    [columnHelper]
  );

  // Create table instance
  const table = useReactTable({
    data: duplicates,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Only render the card if there are duplicates
  if (loading || duplicates.length === 0) {
    return null;
  }

  return (
    <Card>
      <Card.Body>
        <Card.Title className="mb-3">⚠️ Duplicate Student Records Detected</Card.Title>

        {error && (
          <Alert variant="danger" className="my-3">
            {error}
          </Alert>
        )}

        {!error && duplicates.length > 0 && (
          <>
            <div className="mb-3">
              <strong>Total number of duplicate students: {duplicates.length}</strong>
              <p>These student names appear in more than one contact in TFun.  Please remove one from TFun.
                You can now relate one Contact to another through the Contact &gt; General &gt; Related Contact text field.</p>
            </div>
          <div>
            <Table striped bordered hover variant="dark">
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: ' ▲',
                          desc: ' ▼',
                        }[header.column.getIsSorted() as string] ?? null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default DuplicateStudentsCard;
